import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, StatCard, EmptyState } from "@/components/widgets";
import { naira } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Plus, Fuel, Wrench, Sparkles, ParkingSquare, ShieldCheck, Disc, Cog, TrendingUp, Trash2 } from "lucide-react";

const CATS = [
  { id: "fuel", label: "Fuel", icon: Fuel },
  { id: "repairs", label: "Repairs", icon: Wrench },
  { id: "servicing", label: "Servicing", icon: Cog },
  { id: "car wash", label: "Car Wash", icon: Sparkles },
  { id: "parking", label: "Parking", icon: ParkingSquare },
  { id: "insurance", label: "Insurance", icon: ShieldCheck },
  { id: "tyres", label: "Tyres", icon: Disc },
  { id: "tolls", label: "Tolls", icon: TrendingUp },
  { id: "other", label: "Other", icon: Wallet },
];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "fuel", amount: "", liters: "", note: "" });

  const load = () => {
    api.get("/expenses").then((r) => setItems(r.data)).catch(() => {});
    api.get("/expenses/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.amount) { toast.error("Enter an amount."); return; }
    try {
      await api.post("/expenses", {
        category: form.category, amount: parseFloat(form.amount),
        liters: form.liters ? parseFloat(form.liters) : null, note: form.note,
      });
      toast.success("Expense logged.");
      setOpen(false); setForm({ category: "fuel", amount: "", liters: "", note: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/expenses/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <PageHeader title="Expenses" subtitle="See where your money dey go." icon={Wallet}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-expense-btn" className="rounded-full bg-emerald-500 hover:bg-emerald-600 gap-2"><Plus size={18} /> <span className="hidden sm:inline">Add</span></Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle>Log an expense</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Category</Label>
                  <select data-testid="expense-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 h-11 w-full rounded-xl border border-zinc-200 px-3 text-sm">
                    {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div><Label>Amount (₦)</Label><Input data-testid="expense-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="15000" className="mt-1.5 h-11 rounded-xl" /></div>
                {form.category === "fuel" && <div><Label>Litres (optional)</Label><Input data-testid="expense-liters" type="number" value={form.liters} onChange={(e) => setForm({ ...form, liters: e.target.value })} placeholder="30" className="mt-1.5 h-11 rounded-xl" /></div>}
                <div><Label>Note (optional)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5 h-11 rounded-xl" /></div>
              </div>
              <DialogFooter><Button data-testid="expense-save" onClick={add} className="rounded-full bg-emerald-500 hover:bg-emerald-600">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="This Month" value={naira(summary.month_total)} icon={Wallet} tone="emerald" />
          <StatCard label="All Time" value={naira(summary.total)} icon={TrendingUp} />
          <StatCard label="Fuel / Litre" value={summary.avg_fuel_price_per_liter ? naira(summary.avg_fuel_price_per_liter) : "—"} icon={Fuel} tone="amber" />
          <StatCard label="Cost / km" value={summary.cost_per_km ? naira(summary.cost_per_km) : "—"} icon={TrendingUp} />
        </div>
      )}

      {summary && Object.keys(summary.by_category).length > 0 && (
        <div className="rounded-3xl border border-zinc-200 bg-white p-5">
          <h3 className="font-display font-semibold mb-4">By category</h3>
          <div className="space-y-3">
            {Object.entries(summary.by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
              const pct = Math.round((amt / summary.total) * 100);
              return (
                <div key={cat}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize text-zinc-600">{cat}</span>
                    <span className="font-medium text-zinc-800">{naira(amt)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-zinc-100"><div className="h-2 rounded-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h3 className="mb-3 font-display font-semibold">Recent</h3>
        {items.length === 0 ? (
          <EmptyState icon={Wallet} title="No expenses yet" text="Log your first fuel or repair to start tracking." />
        ) : (
          <div className="space-y-2">
            {items.slice(0, 20).map((e) => {
              const cat = CATS.find((c) => c.id === e.category) || CATS[8];
              return (
                <div key={e.id} className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="expense-item">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-zinc-100 text-zinc-500"><cat.icon size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium capitalize text-zinc-800">{cat.label}</div>
                    <div className="text-xs text-zinc-400">{new Date(e.date).toLocaleDateString()} {e.note && `• ${e.note}`}</div>
                  </div>
                  <div className="font-semibold text-zinc-800">{naira(e.amount)}</div>
                  <button onClick={() => del(e.id)} className="text-zinc-300 hover:text-red-500"><Trash2 size={16} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
